import type {
  ResidentReportBatchLoader,
  ResidentReportModel,
} from "../types/resident-report.types";

type ResidentDemoSeed = {
  id: string;
  name: string;
  initials: string;
  neighborhood: string;
  npu: string;
  councilDistrict: string;
  memberSince: string;
  lastActive: string;
  tags: string[];
  attendanceRate: number;
  eventsAttended: number;
  totalEvents: number;
  hoursEngaged: number;
  attendanceStreakWeeks: number;
  longestAttendanceStreakWeeks: number;
  points: number;
  pointsRedeemed: number;
  primaryProgram: string;
  primaryDepartment: string;
  primaryStatus: string;
  primaryInterest: string;
  primaryInterestRate: number;
  participationGap: string | null;
};

const activityByMonth = [
  { month: "Oct", eventsAttended: 2 },
  { month: "Nov", eventsAttended: 3 },
  { month: "Dec", eventsAttended: 3 },
  { month: "Jan", eventsAttended: 2 },
  { month: "Feb", eventsAttended: 4 },
  { month: "Mar", eventsAttended: 5 },
  { month: "Apr", eventsAttended: 6 },
  { month: "May", eventsAttended: 5 },
  { month: "Jun", eventsAttended: 6 },
  { month: "Jul", eventsAttended: 5 },
  { month: "Aug", eventsAttended: 3 },
  { month: "Sep", eventsAttended: 4 },
];

function createResidentReport(seed: ResidentDemoSeed): ResidentReportModel {
  const isJordan = seed.id === "jordan-mitchell";
  const residentsReferred = isJordan ? 3 : Math.max(0, Math.round(seed.eventsAttended / 16));
  const engagementScore = isJordan
    ? 78
    : Math.min(98, Math.round(seed.attendanceRate * 0.8 + seed.attendanceStreakWeeks * 1.2));
  const dropOffRisk =
    seed.attendanceRate >= 75 ? "Low" : seed.attendanceRate >= 60 ? "Medium" : "High";
  const events = isJordan
    ? [
        {
          id: `${seed.id}-garden`,
          name: "Community Garden Program",
          department: "Parks & Recreation",
          eventDate: "2026-09-20",
          attendance: "18 of 19",
          attendanceRate: 95,
          engagedMinutes: null,
          status: "On track",
        },
        {
          id: `${seed.id}-digital-skills`,
          name: "Digital Skills Workshops",
          department: "Atlanta Information Management",
          eventDate: "2026-09-16",
          attendance: "10 of 12",
          attendanceRate: 83,
          engagedMinutes: null,
          status: "On track",
        },
        {
          id: `${seed.id}-npu-meeting`,
          name: "Adair Park Area (NPU V) Meetings",
          department: "City Planning",
          eventDate: "2026-09-08",
          attendance: "8 of 9",
          attendanceRate: 89,
          engagedMinutes: null,
          status: "On track",
        },
        {
          id: `${seed.id}-tree-planting`,
          name: "Tree Planting Volunteer Days",
          department: "Parks & Recreation",
          eventDate: "2026-08-23",
          attendance: "4 of 4",
          attendanceRate: 100,
          engagedMinutes: null,
          status: "Completed",
        },
        {
          id: `${seed.id}-senior-fitness`,
          name: "Senior Fitness",
          department: "Parks & Recreation",
          eventDate: "2026-08-12",
          attendance: "3 of 6",
          attendanceRate: 50,
          engagedMinutes: null,
          status: "Slipping",
        },
      ]
    : [
        {
          id: `${seed.id}-primary`,
          name: seed.primaryProgram,
          department: seed.primaryDepartment,
          eventDate: seed.lastActive,
          attendance: `${seed.eventsAttended} of ${seed.totalEvents}`,
          attendanceRate: seed.attendanceRate,
          engagedMinutes: null,
          status: seed.primaryStatus,
        },
        {
          id: `${seed.id}-digital-skills`,
          name: "Digital Skills Workshops",
          department: "Atlanta Information Management",
          eventDate: "2026-09-16",
          attendance: "10 of 12",
          attendanceRate: 83,
          engagedMinutes: null,
          status: "On track",
        },
        {
          id: `${seed.id}-npu-meeting`,
          name: `${seed.neighborhood} Area (${seed.npu}) Meetings`,
          department: "City Planning",
          eventDate: "2026-09-08",
          attendance: "8 of 9",
          attendanceRate: 89,
          engagedMinutes: null,
          status: "On track",
        },
        {
          id: `${seed.id}-tree-planting`,
          name: "Tree Planting Volunteer Days",
          department: "Parks & Recreation",
          eventDate: "2026-08-23",
          attendance: "4 of 4",
          attendanceRate: 100,
          engagedMinutes: null,
          status: "Completed",
        },
        {
          id: `${seed.id}-senior-fitness`,
          name: "Senior Fitness",
          department: "Parks & Recreation",
          eventDate: "2026-08-12",
          attendance: "3 of 6",
          attendanceRate: 50,
          engagedMinutes: null,
          status: "Slipping",
        },
      ];

  return {
    resident: {
      id: seed.id,
      name: seed.name,
      initials: seed.initials,
      neighborhood: seed.neighborhood,
      npu: seed.npu,
      councilDistrict: seed.councilDistrict,
      memberSince: seed.memberSince,
      lastActive: seed.lastActive,
      tags: seed.tags,
    },
    engagement: {
      score: engagementScore,
      scoreDetail: isJordan
        ? "▲ 12 vs last quarter · Champion"
        : `${seed.attendanceRate >= 75 ? "On track" : "Needs follow-up"} this quarter`,
      dropOffRisk,
      dropOffRiskDetail: isJordan
        ? "1 program slipping"
        : dropOffRisk === "Low"
          ? "Attendance remains consistent"
          : "Review recent attendance",
    },
    summary: {
      attendanceRate: seed.attendanceRate,
      eventsAttended: seed.eventsAttended,
      totalEvents: seed.totalEvents,
      lastActive: seed.lastActive,
    },
    metrics: {
      eventsJoined: 5,
      attendanceRate: seed.attendanceRate,
      eventsAttended: seed.eventsAttended,
      hoursEngaged: seed.hoursEngaged,
      cityAverageHours: 14.2,
      attendanceStreakWeeks: seed.attendanceStreakWeeks,
      longestAttendanceStreakWeeks: seed.longestAttendanceStreakWeeks,
      points: seed.points,
      pointsRedeemed: seed.pointsRedeemed,
      departments: 3,
      residentsReferred,
    },
    events,
    recentEngagement: isJordan
      ? [
          {
            id: `${seed.id}-first-touch`,
            date: "2025-03-01",
            title: "First touch: Community Garden",
            description: "Signed up at Parks pop-up event.",
          },
          {
            id: `${seed.id}-second-program`,
            date: "2025-06-01",
            title: "Second program: Digital Skills",
            description: "Referred by garden coordinator.",
          },
          {
            id: `${seed.id}-civic-step`,
            date: "2025-10-01",
            title: "Civic step: Adair Park area (NPU V) meetings",
            description: "Attends 8 of 9 since joining.",
          },
          {
            id: `${seed.id}-volunteer`,
            date: "2026-04-01",
            title: "Volunteer: Tree Planting Days",
            description: "Brought 3 new residents.",
          },
          {
            id: `${seed.id}-current`,
            date: "2026-07-01",
            title: "Leader: Garden plot captain",
            description: "Now onboards new members.",
            isCurrent: true,
          },
        ]
      : [
          {
            id: `${seed.id}-first-touch`,
            date: seed.memberSince,
            title: `First touch: ${seed.primaryProgram}`,
            description: `Signed up through ${seed.primaryDepartment}.`,
          },
          {
            id: `${seed.id}-second-program`,
            date: "2025-10-01",
            title: "Second program: Digital Skills",
            description: "Referred by a community coordinator.",
          },
          {
            id: `${seed.id}-current`,
            date: seed.lastActive,
            title: `Current activity: ${seed.primaryProgram}`,
            description: "Most recent recorded participation.",
            isCurrent: true,
          },
        ],
    activityByMonth,
    interests: [
      {
        id: `${seed.id}-primary-interest`,
        name: seed.primaryInterest,
        isStated: true,
        participationRate: seed.primaryInterestRate,
      },
      { id: `${seed.id}-technology`, name: "Technology", isStated: true, participationRate: 23 },
      {
        id: `${seed.id}-civic-planning`,
        name: "Civic & Planning",
        isStated: false,
        participationRate: 19,
      },
      {
        id: `${seed.id}-health-fitness`,
        name: "Health & Fitness",
        isStated: true,
        participationRate: 7,
      },
      {
        id: `${seed.id}-arts-culture`,
        name: "Arts & Culture",
        isStated: true,
        participationRate: 0,
      },
    ],
    activitySummary:
      "Peaks in spring and summer; attends most on Saturdays. No gap longer than 3 weeks this year.",
    participationGap: seed.participationGap,
    residentVoice: isJordan
      ? {
          quote:
            "I keep coming back because I can see how the garden and neighborhood meetings change things around me.",
          source: "September 2026 check-in",
          themes: ["Green space", "Community leadership", "Youth opportunities"],
        }
      : {
          quote: `I want more updates about ${seed.primaryInterest.toLowerCase()} opportunities close to home.`,
          source: "Most recent preference check-in",
          themes: [seed.primaryInterest, "Local access", "Program updates"],
        },
    accessPreferences: isJordan
      ? {
          preferredChannel: "SMS",
          preferredTime: "Weekday evenings",
          accessNeeds: ["Childcare when possible", "Transit-friendly locations"],
          language: "English",
        }
      : {
          preferredChannel: "SMS",
          preferredTime: "Weekday evenings",
          accessNeeds: ["Transit-friendly locations"],
          language: "English",
        },
    nextBestAction: isJordan
      ? {
          title: "Invite Jordan to lead an Arts & Culture pop-up",
          detail:
            "Jordan stated interest in arts but has not joined an activity. A peer-led invitation uses their existing volunteer relationship.",
          owner: "Parks & Recreation coordinator",
          timing: "Within 7 days",
        }
      : {
          title: `Send a tailored ${seed.primaryInterest} invitation`,
          detail:
            "Connect the resident's stated interest to a nearby session and include the preferred contact channel.",
          owner: "Program coordinator",
          timing: "Before the next local session",
        },
  };
}

const residentDemoSeeds: ResidentDemoSeed[] = [
  {
    id: "jordan-mitchell",
    name: "Jordan Mitchell",
    initials: "JM",
    neighborhood: "Adair Park",
    npu: "NPU V",
    councilDistrict: "12",
    memberSince: "2025-03-01",
    lastActive: "2026-09-20",
    tags: ["Multi-program", "Volunteer lead", "Consented to data sharing"],
    attendanceRate: 86,
    eventsAttended: 43,
    totalEvents: 50,
    hoursEngaged: 62.5,
    attendanceStreakWeeks: 9,
    longestAttendanceStreakWeeks: 14,
    points: 1240,
    pointsRedeemed: 800,
    primaryProgram: "Community Garden Program",
    primaryDepartment: "Parks & Recreation",
    primaryStatus: "On track",
    primaryInterest: "Environment & Parks",
    primaryInterestRate: 51,
    participationGap:
      "Gap: stated Arts & Culture interest at sign-up, but has not attended any arts programming.",
  },
  {
    id: "taylor-nguyen",
    name: "Taylor Nguyen",
    initials: "TN",
    neighborhood: "Grant Park",
    npu: "NPU V",
    councilDistrict: "12",
    memberSince: "2025-04-15",
    lastActive: "2026-09-19",
    tags: ["Multi-program", "Digital skills"],
    attendanceRate: 79,
    eventsAttended: 31,
    totalEvents: 39,
    hoursEngaged: 44,
    attendanceStreakWeeks: 6,
    longestAttendanceStreakWeeks: 9,
    points: 980,
    pointsRedeemed: 540,
    primaryProgram: "Digital Skills Workshops",
    primaryDepartment: "Atlanta Information Management",
    primaryStatus: "On track",
    primaryInterest: "Technology",
    primaryInterestRate: 58,
    participationGap: "Gap: has not yet attended a civic planning activity.",
  },
  {
    id: "marcus-reed",
    name: "Marcus Reed",
    initials: "MR",
    neighborhood: "Ben Hill",
    npu: "NPU P",
    councilDistrict: "9",
    memberSince: "2025-06-01",
    lastActive: "2026-09-17",
    tags: ["New participant", "Youth sports"],
    attendanceRate: 68,
    eventsAttended: 22,
    totalEvents: 32,
    hoursEngaged: 31.5,
    attendanceStreakWeeks: 3,
    longestAttendanceStreakWeeks: 6,
    points: 640,
    pointsRedeemed: 230,
    primaryProgram: "Youth Sports Leagues",
    primaryDepartment: "Parks & Recreation",
    primaryStatus: "Needs outreach",
    primaryInterest: "Youth Development",
    primaryInterestRate: 47,
    participationGap: "Gap: lower attendance during weeks without transportation support.",
  },
  {
    id: "aisha-williams",
    name: "Aisha Williams",
    initials: "AW",
    neighborhood: "Adamsville",
    npu: "NPU H",
    councilDistrict: "11",
    memberSince: "2024-11-10",
    lastActive: "2026-09-18",
    tags: ["Community advocate", "Repeat participant"],
    attendanceRate: 91,
    eventsAttended: 48,
    totalEvents: 53,
    hoursEngaged: 71,
    attendanceStreakWeeks: 12,
    longestAttendanceStreakWeeks: 16,
    points: 1560,
    pointsRedeemed: 930,
    primaryProgram: "Neighborhood Health Collaborative",
    primaryDepartment: "Health & Wellness",
    primaryStatus: "On track",
    primaryInterest: "Health & Fitness",
    primaryInterestRate: 63,
    participationGap: null,
  },
  {
    id: "devon-brooks",
    name: "Devon Brooks",
    initials: "DB",
    neighborhood: "Vine City",
    npu: "NPU L",
    councilDistrict: "3",
    memberSince: "2025-08-12",
    lastActive: "2026-09-12",
    tags: ["Workforce referral", "New resident"],
    attendanceRate: 62,
    eventsAttended: 18,
    totalEvents: 29,
    hoursEngaged: 26,
    attendanceStreakWeeks: 2,
    longestAttendanceStreakWeeks: 5,
    points: 420,
    pointsRedeemed: 120,
    primaryProgram: "Workforce Readiness",
    primaryDepartment: "Economic Opportunity",
    primaryStatus: "Follow up",
    primaryInterest: "Economic Opportunity",
    primaryInterestRate: 41,
    participationGap:
      "Gap: stated housing interest but has not connected with an assistance program.",
  },
  {
    id: "elena-garcia",
    name: "Elena Garcia",
    initials: "EG",
    neighborhood: "Bankhead",
    npu: "NPU K",
    councilDistrict: "2",
    memberSince: "2024-09-03",
    lastActive: "2026-09-21",
    tags: ["Volunteer lead", "Spanish language preference"],
    attendanceRate: 88,
    eventsAttended: 39,
    totalEvents: 44,
    hoursEngaged: 58,
    attendanceStreakWeeks: 10,
    longestAttendanceStreakWeeks: 13,
    points: 1320,
    pointsRedeemed: 760,
    primaryProgram: "Financial Literacy Series",
    primaryDepartment: "Mayor's Office",
    primaryStatus: "On track",
    primaryInterest: "Financial Literacy",
    primaryInterestRate: 55,
    participationGap: null,
  },
  {
    id: "samir-patel",
    name: "Samir Patel",
    initials: "SP",
    neighborhood: "Kirkwood",
    npu: "NPU O",
    councilDistrict: "5",
    memberSince: "2025-02-20",
    lastActive: "2026-09-15",
    tags: ["Multi-program", "Parent"],
    attendanceRate: 74,
    eventsAttended: 29,
    totalEvents: 39,
    hoursEngaged: 38.5,
    attendanceStreakWeeks: 5,
    longestAttendanceStreakWeeks: 8,
    points: 810,
    pointsRedeemed: 410,
    primaryProgram: "Family Recreation Nights",
    primaryDepartment: "Parks & Recreation",
    primaryStatus: "On track",
    primaryInterest: "Family Activities",
    primaryInterestRate: 49,
    participationGap: "Gap: evening attendance declines when childcare is unavailable.",
  },
  {
    id: "nicole-harris",
    name: "Nicole Harris",
    initials: "NH",
    neighborhood: "Thomasville Heights",
    npu: "NPU Z",
    councilDistrict: "1",
    memberSince: "2025-10-05",
    lastActive: "2026-09-10",
    tags: ["SMS outreach", "New participant"],
    attendanceRate: 56,
    eventsAttended: 14,
    totalEvents: 25,
    hoursEngaged: 19,
    attendanceStreakWeeks: 1,
    longestAttendanceStreakWeeks: 4,
    points: 330,
    pointsRedeemed: 80,
    primaryProgram: "Senior Fitness",
    primaryDepartment: "Parks & Recreation",
    primaryStatus: "Slipping",
    primaryInterest: "Health & Fitness",
    primaryInterestRate: 32,
    participationGap: "Gap: needs transportation support for regular attendance.",
  },
  {
    id: "omar-johnson",
    name: "Omar Johnson",
    initials: "OJ",
    neighborhood: "Midtown",
    npu: "NPU E",
    councilDistrict: "6",
    memberSince: "2024-07-18",
    lastActive: "2026-09-22",
    tags: ["Civic leader", "Multi-program"],
    attendanceRate: 93,
    eventsAttended: 56,
    totalEvents: 60,
    hoursEngaged: 82,
    attendanceStreakWeeks: 15,
    longestAttendanceStreakWeeks: 20,
    points: 1880,
    pointsRedeemed: 1100,
    primaryProgram: "Neighborhood (NPU) Meetings",
    primaryDepartment: "City Planning",
    primaryStatus: "Completed",
    primaryInterest: "Civic & Planning",
    primaryInterestRate: 68,
    participationGap: null,
  },
  {
    id: "riley-thompson",
    name: "Riley Thompson",
    initials: "RT",
    neighborhood: "Collier Heights",
    npu: "NPU I",
    councilDistrict: "10",
    memberSince: "2025-01-25",
    lastActive: "2026-09-14",
    tags: ["Arts interest", "Outreach needed"],
    attendanceRate: 59,
    eventsAttended: 16,
    totalEvents: 27,
    hoursEngaged: 23,
    attendanceStreakWeeks: 2,
    longestAttendanceStreakWeeks: 5,
    points: 380,
    pointsRedeemed: 150,
    primaryProgram: "Arts and Culture Nights",
    primaryDepartment: "Arts & Culture",
    primaryStatus: "Follow up",
    primaryInterest: "Arts & Culture",
    primaryInterestRate: 36,
    participationGap: "Gap: has not returned after the first Arts and Culture event.",
  },
];

export const residentReportDemos = residentDemoSeeds.map(createResidentReport);

export const residentReportDemoLoader: ResidentReportBatchLoader = async ({
  cursor,
  pageSize,
  signal,
}) => {
  if (signal.aborted) throw new DOMException("The request was aborted", "AbortError");

  const startIndex = cursor ? Number.parseInt(cursor, 10) : 0;
  const safeStartIndex = Number.isNaN(startIndex) ? 0 : startIndex;
  const items = residentReportDemos.slice(safeStartIndex, safeStartIndex + pageSize);
  const nextIndex = safeStartIndex + items.length;

  return {
    items,
    nextCursor: nextIndex < residentReportDemos.length ? String(nextIndex) : null,
  };
};
